import React, { useEffect, useState } from 'react';
import { CheckCircle2, Lock, Unlock, AlertTriangle, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * BuildConfirmationStrip · Go-Live Batch S · manual build lock
 *
 * A shared strip rendered in three places:
 *   1. Builder tabs (below the Push button) — variant='builder'
 *   2. Components tab (top of tab)           — variant='components'
 *   3. (Later) PerChildComponentsReview runner card — variant='runner'
 *
 * Reads /appraisals/{id}/build/confirmation on mount + on external
 * refreshes (via `refreshKey`). Shows either:
 *   • confirmed state (green banner + revoke ghost link + "View Quote
 *     Package" + "Send Quote Package" quick actions)
 *   • unconfirmed state (neutral banner + green "Confirm Build" button)
 *
 * `onChange` fires after any confirm/revoke so the parent can refresh
 * downstream state (e.g. component_lines re-fetch, header chip refresh).
 */
export default function BuildConfirmationStrip({
  appraisalId,
  variant = 'components',   // 'builder' | 'components'
  builderLabel = '',        // 'reroof' | 'repairs' | 'restoration' | 'maintenance' | 'guttering'
  refreshKey = 0,
  onChange = () => {},
  onOpenQuotePackage = null,  // optional callback — receives no args, opens preview
  onOpenSendQuoteModal = null, // optional callback — opens EmailModal in Send Quote Package mode
}) {
  const { api } = useAuth();
  const [bc, setBc] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRevoke, setShowRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    if (!appraisalId) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/appraisals/${appraisalId}/build/confirmation`);
      setBc(data?.build_confirmation || null);
      setHistory(data?.build_confirmation_history || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load confirmation state');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appraisalId, refreshKey]);

  const doConfirm = async () => {
    setBusy(true);
    setError('');
    try {
      await api.post(`/appraisals/${appraisalId}/build/confirm`, {
        source: 'manual',
        builder: builderLabel || variant,
      });
      await load();
      onChange();
      window.dispatchEvent(new Event('rtr:build-confirmation-changed'));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Confirm failed');
    } finally {
      setBusy(false);
    }
  };

  const doRevoke = async () => {
    if (revokeReason.trim().length < 3) {
      setError('Reason must be at least 3 characters.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.post(`/appraisals/${appraisalId}/build/revoke`, {
        reason: revokeReason.trim(),
      });
      setShowRevoke(false);
      setRevokeReason('');
      await load();
      onChange();
      window.dispatchEvent(new Event('rtr:build-confirmation-changed'));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const confirmed = !!bc?.confirmed;

  // ── UNCONFIRMED ─────────────────────────────────────────────────────
  if (!confirmed) {
    return (
      <>
      <div
        data-testid={`build-confirm-strip-${variant}-unconfirmed`}
        className="my-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Unlock size={16} className="text-slate-500 shrink-0" />
          <div className="text-xs text-slate-700 min-w-0">
            <div className="font-semibold">Build not confirmed</div>
            <div className="text-[11px] text-slate-500 truncate">
              {variant === 'builder'
                ? 'Push components then confirm to lock this build against further edits.'
                : 'Confirm when the component list is final. Locks the component lines, Quotation Type, diagram pushes and re-pushes. Required before Save Quote PDF; click Revise to edit again.'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={doConfirm}
          disabled={busy}
          data-testid={`build-confirm-btn-${variant}`}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          <CheckCircle2 size={14} />
          {busy ? 'Confirming…' : 'Confirm Build'}
        </button>
      </div>
      {error && (
        <div data-testid={`build-confirm-error-${variant}`} className="-mt-1 mb-3 text-xs text-red-700 bg-red-50 rounded p-2">
          <AlertTriangle size={12} className="inline mr-1" />
          {error}
        </div>
      )}
      </>
    );
  }

  // ── CONFIRMED ────────────────────────────────────────────────────────
  const when = (bc.confirmed_at || '').slice(0, 16).replace('T', ' ');
  const who = bc.confirmed_by_name || 'operator';
  const total = bc.snapshot?.total || 0;
  const lines = bc.line_count || 0;

  return (
    <>
      <div
        data-testid={`build-confirm-strip-${variant}-confirmed`}
        className="my-3 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Lock size={16} className="text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-xs text-emerald-900 min-w-0">
              <div className="font-semibold">
                ✓ Build Confirmed · {lines} line{lines === 1 ? '' : 's'}
                {total > 0 && (
                  <span className="ml-1 text-emerald-700">
                    · ${total.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-emerald-700">
                Locked by <b>{who}</b> · {when} · source: {bc.source || 'manual'}
                {bc.builder && <> · {bc.builder}</>}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowRevoke(true)}
            data-testid={`build-revoke-btn-${variant}`}
            className="shrink-0 text-[11px] text-emerald-800 hover:text-red-700 underline underline-offset-2"
          >
            Revoke
          </button>
        </div>
        {(onOpenQuotePackage || onOpenSendQuoteModal) && (
          <div className="mt-2.5 pt-2.5 border-t border-emerald-200 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-emerald-800 font-semibold">Next:</span>
            {onOpenQuotePackage && (
              <button
                type="button"
                onClick={onOpenQuotePackage}
                data-testid={`view-quote-package-btn-${variant}`}
                className="inline-flex items-center gap-1 rounded-md bg-white ring-1 ring-emerald-300 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
              >
                <ExternalLink size={12} /> View Quote Package
              </button>
            )}
            {onOpenSendQuoteModal && (
              <button
                type="button"
                onClick={onOpenSendQuoteModal}
                data-testid={`send-quote-package-btn-${variant}`}
                className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-orange-700"
              >
                📤 Send Quote Package
              </button>
            )}
          </div>
        )}
        {history.length > 1 && (
          <details className="mt-2 text-[10px] text-emerald-700">
            <summary className="cursor-pointer">Audit trail ({history.length})</summary>
            <ul className="mt-1 ml-3 list-disc">
              {history.slice(-5).reverse().map((h, i) => (
                <li key={i}>
                  <b>{h.action}</b> · {h.at?.slice(0, 16).replace('T', ' ')} · {h.by}
                  {h.reason && <> · <i>{h.reason}</i></>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      {error && (
        <div className="my-2 text-xs text-red-700 bg-red-50 rounded p-2">
          <AlertTriangle size={12} className="inline mr-1" />
          {error}
        </div>
      )}

      {/* Revoke modal */}
      {showRevoke && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !busy && setShowRevoke(false)}>
          <div className="bg-white rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200">
              <h2 className="text-base font-bold text-[#0F172A]">Revoke Build Confirmation</h2>
              <p className="text-xs text-slate-500 mt-1">Unlocks the build for further edits. Audit-logged.</p>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900">
                <AlertTriangle size={12} className="inline mr-1" />
                <b>Heads-up:</b> revoking re-opens the diagram pins, builder pushes, and quote-type editor.
                Any contractor rate edits made after this confirmation are preserved but may be wiped if the
                build is re-pushed.
              </div>
              <label className="block text-xs font-medium text-slate-600">
                Reason (required, min 3 chars)
                <textarea
                  data-testid="build-revoke-reason-input"
                  value={revokeReason}
                  onChange={e => setRevokeReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Client changed scope, pricing correction, diagram revision"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-xs focus:outline-none focus:border-red-500"
                />
              </label>
              {error && <div className="text-xs text-red-700">{error}</div>}
            </div>
            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowRevoke(false)}
                disabled={busy}
                className="text-xs text-slate-600 hover:underline px-2 py-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doRevoke}
                disabled={busy || revokeReason.trim().length < 3}
                data-testid="build-revoke-confirm-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? 'Revoking…' : 'Revoke Confirmation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
